/**
 * CloudShadowLayer — cloud shadows on the ground and everything standing on
 * it (shader wave stage 4, 2026-09-09).
 *
 * The classic projected-texture approach (GameDev.net "Terrain cloud shadows";
 * Godot "Simulating Cloud Shadows Analytically"): a 2D coverage field is
 * addressed by WORLD XZ (an orthographic top-down projection — the sun's
 * elevation only skews the shadow, which the drift offset absorbs), scrolled
 * with the wind, and multiplied into the material's base colour. Applied to
 * the ground, the far tier, the foliage and the structures with the same
 * texture + the same uniforms, every surface darkens under the same cloud.
 *
 * The coverage field is either the caller's (the cloud system's own weather
 * map, so the shadow matches the cloud overhead) or the tileable fBm this
 * module bakes (`generateCloudCoverageTexture`, `cloudCoverageCpu` the
 * oracle). Multiplying the base colour rather than only the direct light is
 * a deliberate simplification: it works on every tier, shadows or not, and
 * a cloud does dim the ambient too.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, positionWorld, texture, smoothstep, mix, materialColor,
} from 'three/tsl';

export interface CloudShadowConfig {
  /**
   * WHERE the shadow lands (audit 16.1, 2026-09-15).
   *   'albedo' (default, the original): multiplies the base COLOUR — which also
   *            dims every lamp, every reflection and metallic F0 under a cloud.
   *   'light'  : multiplies the RECEIVED SHADOW of every shadow-casting light
   *            (`material.receivedShadowNode`), so only that light's direct
   *            diffuse + specular darken and a lamp under a cloud still shines.
   *            Needs a shadow-casting light: with shadows off the hook never
   *            runs and no cloud shadow appears — pick 'albedo' there.
   */
  mode?: 'albedo' | 'light';
  /** Coverage texture (R = coverage 0..1, tileable). Omit to bake one. */
  texture?: THREE.Texture;
  /** World metres per texture repeat (default 6000). */
  worldScale?: number;
  /** Shadow darkness at full coverage 0..1 (default 0.55). */
  strength?: number;
  /** Coverage below this casts nothing (default 0.35). */
  threshold?: number;
  /** Softness of the shadow edge in coverage units (default 0.25). */
  softness?: number;
  /** Seed for the baked texture. */
  seed?: number;
  /** Rotate the projection about world-up (radians) — match the cloud field's own tilted lattice. */
  rotation?: number;
}

export interface CloudShadowHandle {
  /** World-space drift (metres) — advance with the wind each frame. */
  offset: { value: THREE.Vector2 };
  strength: { value: number };
  threshold: { value: number };
  softness: { value: number };
  worldScale: { value: number };
  texture: THREE.Texture;
}

// ── CPU oracle: tileable value-noise fBm on a wrapped lattice ─────────────
function hash2(ix: number, iy: number, seed: number): number {
  let h = (ix * 374761393 + iy * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
function valueNoiseTiled(u: number, v: number, cells: number, seed: number): number {
  const x = u * cells, y = v * cells;
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const fx = x - x0, fy = y - y0;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const w = (ix: number, iy: number): number => hash2(((ix % cells) + cells) % cells, ((iy % cells) + cells) % cells, seed);
  const a = w(x0, y0), b = w(x0 + 1, y0), c = w(x0, y0 + 1), d = w(x0 + 1, y0 + 1);
  return (a * (1 - sx) + b * sx) * (1 - sy) + (c * (1 - sx) + d * sx) * sy;
}

/** Tileable cloud coverage 0..1 at (u, v) in [0,1): four fBm octaves, contrast-shaped. */
export function cloudCoverageCpu(u: number, v: number, seed = 7): number {
  const uu = u - Math.floor(u), vv = v - Math.floor(v);
  let sum = 0, amp = 0.5, cells = 4, norm = 0;
  for (let o = 0; o < 4; o++) {
    sum += valueNoiseTiled(uu, vv, cells, seed + o * 17) * amp;
    norm += amp; amp *= 0.5; cells *= 2;
  }
  const n = sum / norm;
  // shape: push the mid-tones apart so the field reads as clouds and gaps
  const t = (n - 0.42) / 0.3;
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}

/** Bake the oracle into an RGBA8 tileable texture (R = coverage). */
export function generateCloudCoverageTexture(size = 256, seed = 7): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const c = Math.round(cloudCoverageCpu((x + 0.5) / size, (y + 0.5) / size, seed) * 255);
    const i = (y * size + x) * 4;
    data[i] = c; data[i + 1] = c; data[i + 2] = c; data[i + 3] = 255;
  }
  const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.minFilter = THREE.LinearMipmapLinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = true; t.colorSpace = THREE.NoColorSpace; t.needsUpdate = true;
  return t;
}

/** Shadow factor 0..1 (1 = lit) for a coverage value — the shader's rule. */
export function cloudShadowFactorCpu(coverage: number, strength: number, threshold: number, softness: number): number {
  const t = (coverage - threshold) / Math.max(1e-6, softness);
  const s = t < 0 ? 0 : t > 1 ? 1 : t;
  const shade = s * s * (3 - 2 * s);
  return 1 - shade * strength;
}

/**
 * Multiply the material's base colour by the cloud shadow at its world XZ.
 * Composes over an existing colorNode (or the material's colour × map).
 */
export function applyCloudShadowLayer(
  material: THREE.Material & { colorNode?: unknown },
  config?: CloudShadowConfig,
): CloudShadowHandle {
  const tex = config?.texture ?? generateCloudCoverageTexture(256, config?.seed ?? 7);
  const uOffset = uniform(new THREE.Vector2(0, 0));
  const uStrength = uniform(config?.strength ?? 0.55);
  const uThreshold = uniform(config?.threshold ?? 0.35);
  const uSoftness = uniform(config?.softness ?? 0.25);
  const uScale = uniform(config?.worldScale ?? 6000);

  const cr = Math.cos(config?.rotation ?? 0), sr = Math.sin(config?.rotation ?? 0);
  /** 1 = lit, 0 = fully shadowed, at this fragment's world XZ. */
  const litNode = Fn(() => {
    const w = positionWorld.xz.add(uOffset);
    const uv = vec2(w.x.mul(cr).sub(w.y.mul(sr)), w.x.mul(sr).add(w.y.mul(cr))).div(uScale);
    const cov = texture(tex, uv).r;
    const shade = smoothstep(uThreshold, uThreshold.add(uSoftness), cov).mul(uStrength);
    return float(1).sub(shade);
  });
  if ((config?.mode ?? 'albedo') === 'light') {
    // Into the LIGHT: compose over any existing receivedShadowNode.
    const m = material as { receivedShadowNode?: ((shadow: unknown) => unknown) | null };
    const prevShadow = m.receivedShadowNode;
    m.receivedShadowNode = Fn(([shadow]: any) => {
      const s = prevShadow ? float(prevShadow(shadow) as never) : float(shadow);
      return s.mul(litNode());
    }) as never;
  } else {
    const prev = material.colorNode;
    material.colorNode = Fn(() => {
      const base = vec4((prev ?? materialColor) as never);
      const lit = litNode();
      return vec4(mix(base.rgb, base.rgb.mul(lit), float(1)), base.a);
    })();
  }
  material.needsUpdate = true;

  return {
    offset: uOffset as unknown as { value: THREE.Vector2 },
    strength: uStrength as unknown as { value: number },
    threshold: uThreshold as unknown as { value: number },
    softness: uSoftness as unknown as { value: number },
    worldScale: uScale as unknown as { value: number },
    texture: tex,
  };
}

/** Keep the type-level imports referenced (vec2/vec3 used by callers building offsets). */
export const _cloudShadowTslRefs = { vec2, vec3 };
